import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Map,
  UserCheck,
  Users,
  Package,
  ShoppingCart,
  Settings,
  Bell,
  BellRing,
  LogOut,
  X,
  ChevronLeft,
  Activity,
  Star,
  Layers,
  Radar,
  Target,
  Megaphone,
  TrendingUp,
  BarChart2,
  MessageSquare,
  Mail,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/admin' },
  { icon: Map, label: 'Map View', href: '/admin/map' },
  { icon: UserCheck, label: 'Providers', href: '/admin/providers' },
  { icon: Users, label: 'Customers', href: '/admin/customers' },
  { icon: Package, label: 'Listings', href: '/admin/listings' },
  { icon: Star, label: 'Reviews', href: '/admin/reviews' },
  { icon: ShoppingCart, label: 'Orders', href: '/admin/orders' },
  { icon: Target, label: 'Leads', href: '/admin/leads' },
  { icon: TrendingUp, label: 'Engagement', href: '/admin/engagement' },
  { icon: BarChart2, label: 'AppsFlyer', href: '/admin/appsflyer' },
  { icon: Megaphone, label: 'Meta Ads Landing', href: '/admin/meta-ads-landing' },
  { icon: Layers, label: 'Categories', href: '/admin/categories' },
  { icon: Settings, label: 'Configuration', href: '/admin/config' },
  { icon: Bell, label: 'Send Notifications', href: '/admin/notifications' },
  { icon: BellRing, label: 'Notifications Center', href: '/admin/notifications-center' },
  { icon: MessageSquare, label: 'Message Orchestrator', href: '/admin/message-orchestrator' },
  { icon: Mail, label: 'Email Previews', href: '/admin/email-previews' },
  { icon: Activity, label: 'Notification Overview', href: '/admin/notification-overview' },
  { icon: Radar, label: 'Command Center', href: '/admin/ai-ops' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }: SidebarProps) => {
  const location = useLocation();
  const { signOut } = useAuthStore();

  const handleSignOut = async () => {
    await signOut();
  };

  const isActive = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xl font-bold text-gray-900"
              >
                Nepro
              </motion.span>
            )}
          </Link>

          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  active
                    ? 'bg-primary text-white shadow-primary-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : ''}`} />
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="font-medium"
                  >
                    {item.label}
                  </motion.span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Collapse Toggle (Desktop) */}
      <div className="hidden lg:block p-3 border-t border-gray-100">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <ChevronLeft
            className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Sign Out */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={handleSignOut}
          className={`flex items-center gap-3 px-3 py-2.5 w-full text-gray-600 hover:text-error hover:bg-error-light rounded-lg transition-colors ${
            isCollapsed ? 'justify-center' : ''
          }`}
          title={isCollapsed ? 'Sign Out' : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="font-medium">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ${
          isCollapsed ? 'w-[72px]' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-[280px] bg-white z-50 flex flex-col lg:hidden shadow-xl"
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
